<template>
  <v-row>

    <v-col :order="2" :order-lg="1" :lg="departments.length > 0 ? 9 : 12" cols="12">
      <v-card>
        <v-card-title>
          {{ getPageTitle() }}
          <v-spacer></v-spacer>
          <v-icon @click="showSearch = !showSearch">{{ icons.mdiMagnify }}</v-icon>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-text-field v-if="showSearch" outlined v-model="query" :label="$t('Hae')" hide-details class="mb-4"
            clearable />
          <v-row>
            <!-- This column takes full width on xs, half on sm, 1/3 on md, 1/4 on lg -->
            <v-col cols="12" sm="12" md="4" lg="3">
              <div class="pa-4 grey lighten-2">
                <v-select v-model="orderBy" :items="orderingFields" item-text="title" return-object
                  :label="$t('Järjestä')" outlined dense hide-details style="max-width: 200px;" />
              </div>
            </v-col>

            <v-col cols="12" sm="12" md="8" lg="9">
              <div class="pa-4 grey lighten-2">
                <div class="d-flex justify-end mt-2">
                  <v-pagination :total-visible="8" v-model="page" :length="pages"></v-pagination>
                </div>
              </div>
            </v-col>

          </v-row>

          <v-data-table :headers="tableColumns" :items="people" :server-items-length="totalCount" hide-default-header
            hide-default-footer :items-per-page="pageSize" :loading="loading" mobile-breakpoint="0"
            style="cursor: pointer;" @click:row="showPerson">

            <template #[`item.picture`]="{ item }">
              <td style="width: 80px">
                <v-avatar size="48" class="my-2">
                  <v-img v-if="Object.keys(item.picture).length" :src="item.picture.thumbnail"></v-img>
                  <v-icon size="48" v-else color="primary">
                    {{ icons.mdiAccountCircle }}
                  </v-icon>
                </v-avatar>
              </td>
            </template>

            <template #[`item.name`]="{ item }">
              <div class="transition-swing text-subtitle-1 mt-2">

                <template v-if="orderBy.value == 'last_name'">
                  {{ item.last_name + " " + item.first_name }}
                </template>
                <template v-else>
                  {{ item.first_name + " " + item.last_name }}
                </template>

              </div>
              <div class="transition-swing text-caption mb-2">
                {{ item.title }}
              </div>
            </template>

            <template #[`item.phone`]="{ item }">
              <template v-if="item.show_phone && item.phone">
                <a @click.stop="" :href="'tel:' + item.phone">{{ item.phone }}</a>
              </template>
            </template>

            <template #[`item.email`]="{ item }">
              <template v-if="item.show_email && item.email">
                <a @click.stop="" :href="'mailto:' + item.email">{{ item.email }}</a>
              </template>
            </template>
          </v-data-table>
          <div class="d-flex justify-end mt-2">
            <v-pagination :total-visible="8" v-model="page" :length="pages"></v-pagination>
          </div>
        </v-card-text>

      </v-card>
      <v-dialog v-model="dialog" max-width="420px">
        <v-card v-if="viewPerson">
          <v-img v-if="Object.keys(viewPerson.picture).length" :src="viewPerson.picture.full_hd"></v-img>
          <div v-else style="text-align: center">
            <v-icon size="148">
              {{ icons.mdiAccountCircle }}
            </v-icon>
          </div>

          <v-card-title class="justify-center flex-column">
            {{ viewPerson.first_name + " " + viewPerson.last_name }}
          </v-card-title>
          <v-card-subtitle v-if="viewPerson.title" class="text-center">
            {{ viewPerson.title }}
          </v-card-subtitle>
          <v-card-text>
            <v-row v-if="viewPerson.phone">
              <v-col cols="12">
                <v-icon>{{ icons.mdiPhone }}</v-icon>&nbsp;
                <a :href="`tel:${viewPerson.phone}`">{{ viewPerson.phone }}</a>
              </v-col>
              <v-col cols="12" v-if="viewPerson.email">
                <v-icon> {{ icons.mdiEmail }}</v-icon>&nbsp;
                <a :href="`mailto:${viewPerson.email}`">{{
                  viewPerson.email
                }}</a>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="secondary" @click="dialog = false"> Sulje </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-col>
    <v-col :order="1" :order-lg="2" v-if="departments.length > 0" lg="3" cols="12">
      <v-card>
        <v-card-title>{{ $t('Osastot') }}</v-card-title>
        <v-card-text>
          <v-list>
            <v-list-item-group v-model="departmentFilter" color="primary">
              <v-list-item :key="'all'" :value="null">
                <v-list-item-title>
                  {{ $t('Kaikki') }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item v-for="department in departments" :key="department.id" :value="department.id">
                <v-list-item-title style="white-space: normal;">{{ department.name }}</v-list-item-title>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import api from "@/api/";
import { mdiPhone, mdiEmail, mdiAccountCircle, mdiMagnify } from "@mdi/js";
import { useTranslations } from "@core/libs/i18n";
import { i18n } from "@/plugins/i18n";
import useMenus from "@/@core/utils/useMenus";
import useAuth from "@/useAuth";
import goTo from "vuetify/lib/services/goto";
import showdown from "showdown";

export default {
  setup() {
    const people = ref([]);
    const totalCount = ref(0);
    const page = ref(1);
    const pageSize = 20;
    const dialog = ref(false);
    const viewPerson = ref(null);
    const { getTenantTerm } = useMenus();
    const { tenant } = useAuth();
    const options = ref({});
    const loading = ref(true);
    const departments = ref([]);
    const departmentFilter = ref(null);
    const showSearch = ref(false);
    const query = ref("");
    const orderingFields = [
      {
        title: i18n.t('Etunimi'),
        value: 'first_name'
      },
      {
        title: i18n.t('Sukunimi'),
        value: 'last_name'
      }
    ];
    const orderBy = ref(orderingFields[1]);

    const { t } = useTranslations();

    const tableColumns = [
      {
        text: "",
        value: "picture",
        sortable: false,
        width: '200px'
      },
      {
        text: t("Nimi").toUpperCase(),
        value: "name",
        sortable: false,
      },
      {
        text: t("Puhelin").toUpperCase(),
        value: "phone",
        sortable: false,
      },
      {
        text: t("Sähköposti").toUpperCase(),
        value: "email",
        sortable: false,
      },
    ];

    const fetchDeparments = async () => {
      const response = await api.getTenantDepartments();
      departments.value = response.data;
    }

    // fetch data
    const fetchPeople = async () => {
      loading.value = true;

      const ordering = orderBy.value.value == 'last_name' ? 'last_name,first_name' : 'first_name,last_name'

      const response = await api.getPeople(page.value, pageSize, departmentFilter.value, query.value, ordering);
      people.value = response.data.results;
      totalCount.value = response.data.count;

      loading.value = false;
    };

    watch(page, () => {
      fetchPeople();
      goTo(0);
    });

    const init = async () => {
      await fetchDeparments();
      await fetchPeople();
    }

    onMounted(() => {
      init();
    });

    watch(options, () => {
      page.value = options.value.page;
      fetchPeople();
    });

    watch(departmentFilter, () => {
      page.value = 1;
      fetchPeople();
    });

    watch(orderBy, () => {
      page.value = 1;
      fetchPeople();
    });

    const showPerson = async (person) => {
      viewPerson.value = person;
      dialog.value = true;
    };

    const pages = computed(() => {
      return Math.ceil(totalCount.value / pageSize);
    });

    const getPageTitle = () => {
      const tenantTerm = getTenantTerm(
        "people",
        tenant.value.terms?.translations,
        i18n.locale
      );
      return tenantTerm ?? t("Meillä töissä");
    };

    let searchTimeout = null;
    watch(query, () => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        if (query.value && query.value.length > 2) {
          page.value = 1;
          fetchPeople();
        }
        else if (!query.value) {
          fetchPeople();
        }
      }, 600);
    });


    return {
      tableColumns,
      options,
      loading,
      pageSize,
      departments,
      getPageTitle,
      departmentFilter,
      people,
      totalCount,
      showPerson,
      page,
      showSearch,
      pages,
      dialog,
      tenant,
      query,
      viewPerson,
      orderBy,
      orderingFields,
      icons: {
        mdiPhone,
        mdiMagnify,
        mdiEmail,
        mdiAccountCircle,
      },
      t,
    };
  },
};
</script>
